import React, { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Bell, Plus, Search, Trash2, RefreshCw, Calendar, Phone, User, Briefcase, Coffee
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_META = {
  personal: { label: 'Personal', icon: Coffee, color: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  work: { label: 'Work', icon: Briefcase, color: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  call: { label: 'Call', icon: Phone, color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  other: { label: 'Other', icon: Bell, color: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30' },
};

const formatRelative = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  const diff = d - Date.now();
  const abs = Math.abs(diff);
  const mins = Math.round(abs / 60000);
  const hours = Math.round(mins / 60);
  const days = Math.round(hours / 24);
  let label;
  if (mins < 1) label = 'now';
  else if (mins < 60) label = `${mins}m`;
  else if (hours < 24) label = `${hours}h`;
  else label = `${days}d`;
  const past = diff < 0;
  return { label: past ? `${label} ago` : `in ${label}`, past, exact: d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) };
};

const RemindersPage = () => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', remind_at: '', category: 'personal' });

  const fetchReminders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/reminders', { params: { status: statusFilter, ...(search ? { search } : {}) } });
      setReminders(res.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => { fetchReminders(); }, [fetchReminders]);

  const resetForm = () => setForm({ title: '', description: '', remind_at: '', category: 'personal' });

  const handleCreate = async () => {
    if (!form.title.trim()) return toast.error('Title is required');
    try {
      await api.post('/reminders', {
        title: form.title.trim(),
        description: form.description || null,
        remind_at: form.remind_at ? new Date(form.remind_at).toISOString() : null,
        category: form.category,
      });
      toast.success('Reminder added');
      setShowCreate(false);
      resetForm();
      fetchReminders();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleToggleDone = async (r) => {
    try {
      await api.patch(`/reminders/${r.id}`, { done: !r.done });
      fetchReminders();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this reminder?')) return;
    try {
      await api.delete(`/reminders/${id}`);
      fetchReminders();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white p-6" data-testid="reminders-page">
      <div className="max-w-3xl mx-auto space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Bell className="w-8 h-8 text-[#F5A623]" /> Reminders
            </h1>
            <p className="text-sm text-white/60 mt-1">Your private to-dos, calls, and personal notes. We'll ping you when it's time.</p>
          </div>
          <Button onClick={() => { resetForm(); setShowCreate(true); }} className="bg-[#F5A623] text-black hover:bg-[#F5A623]/90" data-testid="add-reminder-btn">
            <Plus className="w-4 h-4 mr-1" /> Add reminder
          </Button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex bg-white/5 rounded-md p-1 gap-1">
            {['pending', 'done', 'all'].map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 text-sm rounded capitalize ${statusFilter === s ? 'bg-[#F5A623] text-black font-medium' : 'text-zinc-400 hover:text-white'}`}
                data-testid={`reminders-filter-${s}`}>
                {s}
              </button>
            ))}
          </div>
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search reminders…"
              className="pl-9 bg-white/5 border-white/10" data-testid="reminders-search" />
          </div>
          <Button variant="outline" size="sm" onClick={fetchReminders} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-sm text-white/50 py-12 text-center">Loading…</div>
        ) : reminders.length === 0 ? (
          <Card className="bg-[#121212] border-white/10">
            <CardContent className="p-10 text-center text-white/50">
              <Bell className="w-10 h-10 mx-auto mb-2 text-white/30" />
              {search ? 'No reminders match.' : statusFilter === 'done' ? 'No completed reminders yet.' : 'No reminders. Add your first one!'}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="reminders-list">
            {reminders.map(r => {
              const cat = CATEGORY_META[r.category] || CATEGORY_META.other;
              const CatIcon = cat.icon;
              const rel = formatRelative(r.remind_at);
              return (
                <Card key={r.id} className={`bg-[#121212] border-white/10 ${r.done ? 'opacity-50' : ''}`}>
                  <CardContent className="p-4 flex items-start gap-3" data-testid={`reminder-${r.id}`}>
                    <Checkbox
                      checked={r.done}
                      onCheckedChange={() => handleToggleDone(r)}
                      className="mt-1"
                      data-testid={`toggle-reminder-${r.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={`${cat.color} border`}>
                          <CatIcon className="w-3 h-3 mr-1" />
                          {cat.label}
                        </Badge>
                        <div className={`font-medium ${r.done ? 'line-through text-white/40' : ''}`}>{r.title}</div>
                      </div>
                      {r.description && (
                        <div className={`text-sm mt-1 whitespace-pre-line ${r.done ? 'text-white/40' : 'text-white/70'}`}>{r.description}</div>
                      )}
                      {rel && (
                        <div className={`text-xs mt-1.5 flex items-center gap-1 ${rel.past && !r.done ? 'text-red-400' : 'text-white/50'}`}
                          title={rel.exact}>
                          <Calendar className="w-3 h-3" />
                          {rel.exact} · {rel.label}
                        </div>
                      )}
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(r.id)} data-testid={`delete-reminder-${r.id}`}>
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#121212] border-white/10 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New reminder</DialogTitle>
          </DialogHeader>
          <div className="space-y-3" data-testid="reminder-dialog">
            <div>
              <Label className="text-sm text-white/70">Title *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Call Sharma about advance" className="bg-white/5 border-white/10"
                data-testid="reminder-title" />
            </div>
            <div>
              <Label className="text-sm text-white/70">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2} placeholder="Optional notes" className="bg-white/5 border-white/10"
                data-testid="reminder-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm text-white/70">Remind me at</Label>
                <Input type="datetime-local" value={form.remind_at} onChange={(e) => setForm({ ...form, remind_at: e.target.value })}
                  className="bg-white/5 border-white/10" data-testid="reminder-remind-at" />
              </div>
              <div>
                <Label className="text-sm text-white/70">Category</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {Object.entries(CATEGORY_META).map(([k, m]) => {
                    const Icon = m.icon;
                    return (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setForm({ ...form, category: k })}
                        className={`px-2 py-1 text-xs rounded border flex items-center gap-1 ${form.category === k ? m.color : 'border-white/10 bg-white/5 text-white/60'}`}
                        data-testid={`reminder-cat-${k}`}>
                        <Icon className="w-3 h-3" /> {m.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={handleCreate} className="bg-[#F5A623] text-black hover:bg-[#F5A623]/90" data-testid="submit-reminder">Add</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RemindersPage;
